import React, { Fragment, useEffect } from "react";
import { useDropzone } from "react-dropzone";

import loadImage from "blueimp-load-image";
import FeatherIcon from "feather-icons-react";

import { canvasToBase64 } from "../../helpers/utils";
import * as style from "./styles.module.scss";
import { isSmallerThanDesktop } from "../../helpers";
import { useWindowDimensions } from "../../helpers/hooks";

import { downloadFormDocument } from "../../api/fontApi";

const AwaitingDrop = () => (
  <div className="d-flex flex-column align-items-center mx-4">
    <FeatherIcon icon="upload-cloud" className="text-lightGrey" />{" "}
    <div
      className={`${style.dropzone__text} d-flex flex-column align-items-center`}
    >
      <p className="mb-0">Drag & drop files here</p>
      <p>or click to browse and upload</p>
    </div>
  </div>
);

const DroppedWrongFile = ({ wrongFileTypeHint }) => (
  <div className="d-flex flex-column align-items-center mx-4">
    <FeatherIcon icon="alert-circle" className="text-danger" />{" "}
    <div
      className={`${style.dropzone__text} d-flex flex-column align-items-center`}
    >
      <p className="mb-0">Wrong file type</p>
      <p className="mb-0">{wrongFileTypeHint}</p>
      <p>Drop another file here or click to browse and upload again</p>
    </div>
  </div>
);

const DroppedFileSuccess = ({
  file,
  isMobile,
  imageMaxHeight,
  imageMaxWidth,
}) => {
  if (!file) {
    return null;
  }
  const isImage = typeof file !== "object";

  if (isImage) {
    const getImageWidth = () => {
      if (imageMaxWidth) return imageMaxWidth;
      return isMobile ? "130px" : "500px";
    };
    return (
      <img
        className="d-flex flex-column align-items-center mx-4 mb-1 w-100 h-100"
        src={`data:image/jpeg;base64,${file}`}
        style={{
          objectFit: "contain",
          maxWidth: getImageWidth(),
          maxHeight: imageMaxHeight,
        }}
        alt="preview"
      />
    );
  }
  return (
    <div
      className="d-flex flex-column align-items-center"
      style={{ marginBottom: "5px" }}
    >
      <FeatherIcon icon="file" className="text-lightGrey" />
      {file.uploaded ? (
        <p
          onClick={() => downloadFormDocument(file.path, file.name)}
          className={style.dropzone__link}
        >
          {file.name}
        </p>
      ) : (
        <p>{file.name}</p>
      )}
    </div>
  );
};

const FileDropzoneNew = ({
  acceptedFileTypes = ".jpg,.jpeg,.png",
  handleChange,
  fileStateName,
  fileParsedToString,
  wrongFileTypeHint,
  handleInvalidType,
  isFileValid,
  fileCategory = "image",
  imageMaxWidth = "100%",
  imageUploadOptions = {},
  validationMessages = [],
  imageAsBase64 = true,
  disabled = false,
}) => {
  const {
    getRootProps,
    getInputProps,
    open,
    acceptedFiles,
    fileRejections,
    isDragActive,
  } = useDropzone({
    multiple: false,
    noClick: true,
    noKeyboard: true,
    accept: acceptedFileTypes,
    disabled,
  });

  const fileNotProvided = validationMessages.length > 0;
  const validationMessage = fileNotProvided ? validationMessages[0] : "";

  useEffect(() => {
    if (acceptedFiles[0]) {
      const file = acceptedFiles[0];
      const isImage = file.type.startsWith("image/");

      if (isImage) {
        loadImage(
          file,
          canvasToBase64((base64Str) => {
            const fileData = imageAsBase64 ? base64Str : file;
            return handleChange(fileStateName, fileData);
          }),
          {
            maxWidth: 800,
            maxHeight: 800,
            contain: true,
            canvas: true,
            orientation: true,
            meta: true,
            ...imageUploadOptions,
          }
        );
      } else {
        handleChange(fileStateName, file);
      }
    }

    if (fileRejections[0] && handleInvalidType) {
      handleInvalidType();
      handleChange(fileStateName, null);
    }
  }, [acceptedFiles, fileRejections]);

  const isWrongFileType =
    !fileParsedToString && (!isFileValid || fileRejections.length > 0);
  const isFileDropped = fileParsedToString || acceptedFiles.length > 0;
  const isAwaitingDrop = !isFileDropped && !isWrongFileType;

  const { windowWidth } = useWindowDimensions();
  const isSmallerThanLarge = isSmallerThanDesktop(windowWidth);

  const calculateImageHeight = () => {
    const dropzoneHeight = "80%";
    return `${Number(dropzoneHeight) - 90}px`;
  };

  return (
    <Fragment>
      <div
        {...getRootProps({ className: "dropzone" })}
        className={`p-1 ${style.dropzone} 
      ${isFileDropped || isDragActive ? style.dropzone_success : ""}`}
        style={{
          width: "100%",
          height: "100%",
          cursor: isFileDropped ? "default" : "pointer",
        }}
        onClick={isFileDropped ? null : open}
      >
        <input {...getInputProps()} />

        {isAwaitingDrop && <AwaitingDrop />}
        {isWrongFileType && (
          <DroppedWrongFile wrongFileTypeHint={wrongFileTypeHint} />
        )}
        {isFileDropped && (
          <DroppedFileSuccess
            file={fileParsedToString}
            isMobile={isSmallerThanLarge}
            imageMaxHeight={calculateImageHeight()}
            imageMaxWidth={imageMaxWidth}
          />
        )}
        {isFileDropped && (
          <button
            className={`btn btn-outline-info text-dark ${style.dropzone__btn}`}
            onClick={open}
            type="button"
          >
            Choose a different {fileCategory}
          </button>
        )}
      </div>
      {validationMessage && (
        <span className="text_invalid fontSize-12" style={{ color: "red" }}>
          {validationMessage}
        </span>
      )}
    </Fragment>
  );
};

export default FileDropzoneNew;
